import { useState } from 'react';
import CustomForm, { FormField } from '../../components/form/custom-form';
import { useCrud } from '../hooks/use-crud';
import { CrudPathParams, CrudResource } from '../models/crud.types';

interface CrudModalProps<T extends object> {
  formId: string;
  resource: CrudResource<T>;
  params?: CrudPathParams;
  mode: 'create' | 'update';
  value?: T;
  onSuccess?: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
}

export default function CrudModal<T extends object>(props: CrudModalProps<T>) {
  const {
    formId,
    resource,
    params,
    mode,
    value,
    onSuccess,
    onSubmittingChange,
  } = props;
  const crud = useCrud(resource, params, undefined, { skipList: true });

  const [isSubmitting, setSubmitting] = useState(false);

  const getFields = (): FormField[] => {
    const fields = resource.form.fields as FormField[];

    if (mode === 'create' || !value) {
      return fields;
    }

    return fields.map((field) => ({
      ...field,
      initalValue: (value as any)[field.name],
    }));
  };

  const onSubmit = (formValues: any, resetFields?: () => void) => {
    setSubmitting(true);
    onSubmittingChange?.(true);

    const promise =
      mode === 'create'
        ? crud.create(formValues)
        : crud.update((value as any)[resource.rowKey], formValues);

    promise
      .then(() => {
        if (mode === 'create' && resetFields) {
          resetFields();
        }

        onSuccess?.();
      })
      .catch(() => null)
      .finally(() => {
        setSubmitting(false);
        onSubmittingChange?.(false);
      });
  };

  return (
    <CustomForm
      key={formId}
      formId={formId}
      fields={getFields()}
      onSubmit={onSubmit}
      loading={isSubmitting}
    />
  );
}
